// client/src/coms/views/canvas_parts/CanvasAreaAssignmentModal.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  CANVAS_AREA_LAYOUTS,
  getCanvasAreaSlotIds,
  sanitizeCanvasAreaAssignments,
} from './canvas_room_view_parts/canvasAreaAssignments';

function modalButtonStyle(primary = false, danger = false) {
  return {
    height: 34,
    borderRadius: 8,
    border: danger ? '1px solid rgba(248,113,113,0.34)' : primary ? '1px solid rgba(96,165,250,0.45)' : '1px solid rgba(255,255,255,0.14)',
    background: danger ? 'rgba(127,29,29,0.25)' : primary ? 'rgba(37,99,235,0.62)' : 'rgba(255,255,255,0.075)',
    color: 'rgba(255,255,255,0.96)',
    padding: '0 12px',
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
  };
}

export default function CanvasAreaAssignmentModal({ open, areaAssignments, availableUsers, onClose, onApply }) {
  const [draft, setDraft] = useState(() => sanitizeCanvasAreaAssignments(areaAssignments));
  const users = useMemo(() => (Array.isArray(availableUsers) ? availableUsers.filter(Boolean) : []), [availableUsers]);
  const slotIds = useMemo(() => getCanvasAreaSlotIds(draft), [draft]);

  useEffect(() => {
    if (!open) return;
    setDraft(sanitizeCanvasAreaAssignments(areaAssignments));
  }, [areaAssignments, open]);

  if (!open) return null;

  const updateDraft = (patch) => {
    setDraft((current) => sanitizeCanvasAreaAssignments({ ...current, ...patch }));
  };

  const assignSlot = (slotId, ownerKey) => {
    const user = users.find((item) => item && item.ownerKey === ownerKey) || null;
    setDraft((current) => {
      const nextSlots = { ...(current.slots || {}) };
      if (!user) {
        delete nextSlots[slotId];
      } else {
        nextSlots[slotId] = {
          ownerKey: user.ownerKey,
          ownerName: user.ownerName || user.ownerKey,
          ownerUserId: user.ownerUserId || 0,
          ownerClientId: user.ownerClientId || '',
        };
      }
      return sanitizeCanvasAreaAssignments({ ...current, slots: nextSlots });
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.48)',
        backdropFilter: 'blur(6px)',
        padding: 18,
        boxSizing: 'border-box',
      }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(620px, 100%)',
          maxHeight: 'min(720px, 92vh)',
          overflow: 'auto',
          borderRadius: 14,
          border: '1px solid rgba(255,255,255,0.16)',
          background: 'rgba(37,37,37,0.985)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.55)',
          color: 'rgba(255,255,255,0.95)',
          padding: 16,
          boxSizing: 'border-box',
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 950, marginBottom: 4 }}>Canvas drawing areas</div>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: 'rgba(255,255,255,0.66)' }}>
              Assign parts of the page to users. The dotted guide is visible on the canvas and each user can only start or continue strokes inside their assigned area.
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ ...modalButtonStyle(false), width: 34, padding: 0, fontSize: 18 }}>×</button>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13, fontWeight: 900 }}>
          <input
            type="checkbox"
            checked={!!draft.enabled}
            onChange={(event) => updateDraft({ enabled: event.target.checked })}
          />
          Enable drawing areas
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 900, color: 'rgba(255,255,255,0.78)' }}>Layout</div>
          <select
            value={draft.layout}
            onChange={(event) => updateDraft({ layout: event.target.value })}
            style={{
              height: 34,
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(0,0,0,0.22)',
              color: 'rgba(255,255,255,0.96)',
              padding: '0 9px',
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {CANVAS_AREA_LAYOUTS.map((layout) => (
              <option key={layout.id} value={layout.id}>{layout.label}</option>
            ))}
          </select>

          {(draft.layout === 'vertical' || draft.layout === 'quarters') ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 900, color: 'rgba(255,255,255,0.78)' }}>Vertical line</div>
              <input type="range" min="10" max="90" step="1" value={draft.splitX} onChange={(event) => updateDraft({ splitX: Number(event.target.value) })} />
            </>
          ) : null}

          {(draft.layout === 'horizontal' || draft.layout === 'quarters') ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 900, color: 'rgba(255,255,255,0.78)' }}>Horizontal line</div>
              <input type="range" min="10" max="90" step="1" value={draft.splitY} onChange={(event) => updateDraft({ splitY: Number(event.target.value) })} />
            </>
          ) : null}
        </div>

        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {slotIds.map((slot) => (
            <label
              key={slot.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '130px 1fr',
                gap: 10,
                alignItems: 'center',
                padding: 8,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.055)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 950 }}>{slot.label}</span>
              <select
                value={(draft.slots && draft.slots[slot.id] && draft.slots[slot.id].ownerKey) || ''}
                onChange={(event) => assignSlot(slot.id, event.target.value)}
                style={{
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(0,0,0,0.22)',
                  color: 'rgba(255,255,255,0.96)',
                  padding: '0 9px',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                <option value="">Nobody</option>
                {users.map((user) => (
                  <option key={user.ownerKey} value={user.ownerKey}>{user.ownerName || user.ownerKey}</option>
                ))}
              </select>
            </label>
          ))}
        </div>

        {!users.length ? (
          <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.62)' }}>
            No user layers found yet. Users appear here after they join or create their layer.
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={() => onApply(sanitizeCanvasAreaAssignments({ ...draft, enabled: false }))} style={modalButtonStyle(false, true)}>Disable</button>
          <button type="button" onClick={onClose} style={modalButtonStyle(false)}>Cancel</button>
          <button type="button" onClick={() => onApply(sanitizeCanvasAreaAssignments(draft))} style={modalButtonStyle(true)}>Apply</button>
        </div>
      </div>
    </div>
  );
}
